import React, { useEffect, useRef, useState } from 'react';
import styles from './Styles/Campaigns.module.css';
import {
  getPlaybook,
  createPlaybookCampaign,
  updatePlaybookCampaign,
  deletePlaybookCampaign,
  reorderPlaybook,
} from '../../Services/arndAPI';
import Loader from '../../Components/Loader/Loader';
import PageHeader from '../../Components/PageHeader/PageHeader';
import Page from '../../Components/Page/Page';
import StagePicker from './campaign/StagePicker';
import ChannelPicker from './campaign/ChannelPicker';
import MessageEditor from './campaign/MessageEditor';
import SlotEditor from './campaign/SlotEditor';
import PreviewPanel from './campaign/PreviewPanel';
import ImpactModal from './campaign/ImpactModal';
import { CHANNEL_ICON, CHANNEL_LABEL, PLACEHOLDERS, STAGE_LABELS } from './campaign/placeholders';
import { newSlot, slotLabel } from './campaignSlots';
import { campaignSmsContext, worstCaseSms } from './campaignLength';
import { perParentLabel, toPositiveInt } from './campaignLimits';

// SCRUM-449: the automated-campaign playbook. One card per campaign, in
// priority order (card order = priority: the higher card wins a collision).
// Every change that can move an active event's unsent steps — Save and the
// enabled switch — shows the API's dry-run impact first and saves on Confirm.

const NEW_ID = '__new__';

const blankCampaign = () => ({
  id: NEW_ID,
  name: '',
  objective: '',
  enabled: true,
  stages: [],
  channels: ['push', 'sms'],
  slots: [newSlot('repeat', [])],
  content: { title: 'ARND', subject: '', bodies: {} },
  maxPerParent: null,
});

/** What goes over the wire: no id/priority, bodies only for the selected channels. */
export function toPayload(c) {
  const bodies = {};
  for (const ch of c.channels) bodies[ch] = c.content?.bodies?.[ch] ?? '';
  const content = { bodies };
  if (c.channels.includes('push')) content.title = c.content?.title ?? '';
  if (c.channels.includes('email')) content.subject = c.content?.subject ?? '';
  return {
    name: c.name,
    objective: c.objective,
    enabled: c.enabled,
    stages: c.stages,
    channels: c.channels,
    slots: c.slots,
    content,
    maxPerParent: toPositiveInt(c.maxPerParent),
  };
}

const byPriority = (list) => [...list].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));

const PILL_RE = new RegExp(`(\\{(?:${PLACEHOLDERS.join('|')})\\})`, 'g');

/** Body text with the {pills} wrapped in <mark>. */
function Highlighted({ text }) {
  return String(text ?? '')
    .split(PILL_RE)
    .map((part, i) => (i % 2 === 1 ? <mark key={i} className={styles.pill}>{part}</mark> : part));
}

function PerParentEditor({ maxPerParent, onChange }) {
  const changeMax = (value) => onChange({ maxPerParent: toPositiveInt(value) });

  return (
    <div className={styles.slotParams}>
      <span>Per parent: At most</span>
      <input
        type="number"
        min={1}
        step={1}
        aria-label="Max sends per parent"
        value={maxPerParent ?? ''}
        onChange={(e) => changeMax(e.target.value)}
      />
      <span>times</span>
    </div>
  );
}

function CampaignEditor({ draft, setDraft, error, busy, invalid, onValidityChange, onSave, onCancel, onDelete }) {
  const set = (p) => setDraft({ ...draft, ...p });
  const [previewing, setPreviewing] = useState(false);
  const [slotsInvalid, setSlotsInvalid] = useState(false);
  return (
    <div className={styles.editor}>
      <label className={styles.field}>
        Name
        <input value={draft.name} onChange={(e) => set({ name: e.target.value })} />
      </label>
      <label className={styles.field}>
        Objective
        <input value={draft.objective} onChange={(e) => set({ objective: e.target.value })} />
      </label>
      <div className={styles.field}>
        <span>Audience</span>
        <StagePicker value={draft.stages} onChange={(stages) => set({ stages })} />
      </div>
      <div className={styles.field}>
        <span>When</span>
        <SlotEditor slots={draft.slots} onChange={(slots) => set({ slots })} onValidityChange={setSlotsInvalid} />
      </div>
      <div className={styles.field}>
        <PerParentEditor
          key={draft.id}
          maxPerParent={draft.maxPerParent ?? null}
          onChange={(p) => set(p)}
        />
      </div>
      <div className={styles.field}>
        <span>Channels</span>
        <ChannelPicker value={draft.channels} onChange={(channels) => set({ channels })} />
      </div>
      <MessageEditor
        key={draft.id}
        channels={draft.channels}
        title={draft.content?.title ?? ''}
        subject={draft.content?.subject ?? ''}
        bodies={draft.content?.bodies ?? {}}
        onChange={(content) => set({ content })}
        // SCRUM-449: no real event exists here — the same worst case the
        // API's save check uses (40-char event, 15-char slug link; the
        // generic link for a lead-only campaign).
        lengthContext={campaignSmsContext(draft.stages)}
        onValidityChange={onValidityChange}
      />
      {error && <div className={styles.error} role="alert">{error}</div>}
      <div className={styles.actions}>
        {onDelete && (
          <button type="button" className={styles.dangerBtn} onClick={onDelete} disabled={busy}>Delete</button>
        )}
        <button type="button" className={styles.secondaryBtn} onClick={() => setPreviewing((p) => !p)}>Preview</button>
        <button type="button" className={styles.secondaryBtn} onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" className={styles.primaryBtn} onClick={onSave} disabled={busy || invalid || slotsInvalid}>
          Save
        </button>
      </div>
      {previewing && <PreviewPanel campaign={draft} />}
    </div>
  );
}

function CampaignView({ campaign, error, busy, onEdit }) {
  const [previewing, setPreviewing] = useState(false);
  const bodies = campaign.content?.bodies ?? {};
  return (
    <>
      <dl className={styles.rows}>
        <dt>Objective</dt>
        <dd>{campaign.objective}</dd>
        <dt>Audience</dt>
        <dd className={styles.chipRow}>
          {campaign.stages.map((s) => <span key={s} className={styles.chip}>{STAGE_LABELS[s] ?? s}</span>)}
        </dd>
        <dt>When</dt>
        <dd className={styles.chipRow}>
          {campaign.slots.map((s) => <span key={s.id} className={styles.slotChip}>{slotLabel(s)}</span>)}
        </dd>
        <dt>Per parent</dt>
        <dd>{perParentLabel(campaign.maxPerParent)}</dd>
        <dt>Channels</dt>
        <dd className={styles.chipRow}>
          {campaign.channels.map((ch) => <span key={ch}>{CHANNEL_ICON[ch]} {ch}</span>)}
        </dd>
        {campaign.channels.map((ch) => (
          <React.Fragment key={ch}>
            <dt>{CHANNEL_LABEL[ch].replace(/^\S+\s/, '')}</dt>
            <dd className={styles.bodyLine}>
              <span className={styles.bodyText} data-testid={`body-${ch}`}><Highlighted text={bodies[ch]} /></span>
              {ch === 'sms' && (
                <span className={styles.muted}>
                  {worstCaseSms(bodies.sms ?? '', campaignSmsContext(campaign.stages)).segments} seg
                </span>
              )}
            </dd>
          </React.Fragment>
        ))}
      </dl>
      {error && <div className={styles.error} role="alert">{error}</div>}
      <div className={styles.actions}>
        <button type="button" className={styles.secondaryBtn} onClick={onEdit} disabled={busy}>Edit</button>
        <button type="button" className={styles.secondaryBtn} onClick={() => setPreviewing((p) => !p)}>Preview</button>
      </div>
      {previewing && <PreviewPanel campaign={campaign} />}
    </>
  );
}

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [errors, setErrors] = useState({});
  const [pending, setPending] = useState(null); // { id, payload, isNew, impact }
  const [busy, setBusy] = useState(false);
  // SCRUM-449: the open editor's SMS runs past one text — Save and Confirm stay off.
  const [editorInvalid, setEditorInvalid] = useState(false);

  useEffect(() => {
    getPlaybook()
      .then((list) => setCampaigns(byPriority(Array.isArray(list) ? list : [])))
      .catch((e) => setStatus(e.message))
      .finally(() => setLoading(false));
  }, []);

  const setError = (id, msg) => setErrors((e) => ({ ...e, [id]: msg }));

  const startEdit = (c) => {
    setEditingId(c.id);
    setDraft(JSON.parse(JSON.stringify(c)));
    setError(c.id, '');
  };
  const stopEdit = () => {
    setEditingId(null);
    setDraft(null);
    setEditorInvalid(false);
  };

  // Step 1: the dry run. A 400 names the field — shown on the card.
  const requestSave = async (id, payload, isNew) => {
    setError(id, '');
    setBusy(true);
    try {
      const res = isNew
        ? await createPlaybookCampaign(payload, { dryRun: true })
        : await updatePlaybookCampaign(id, payload, { dryRun: true });
      setPending({ id, payload, isNew, impact: res?.impact ?? [] });
    } catch (e) {
      setError(id, e.message);
    } finally {
      setBusy(false);
    }
  };

  // Step 2: Confirm → save for real.
  const confirmSave = async () => {
    const { id, payload, isNew } = pending;
    setBusy(true);
    try {
      if (isNew) {
        const saved = await createPlaybookCampaign(payload);
        setCampaigns((list) => [...list, { ...payload, ...saved }]);
      } else {
        const saved = await updatePlaybookCampaign(id, payload);
        setCampaigns((list) =>
          list.map((c) => (c.id === id ? { ...c, ...payload, ...(saved && typeof saved === 'object' ? saved : {}), id } : c))
        );
      }
      if (editingId === id) stopEdit();
    } catch (e) {
      setError(id, e.message);
    } finally {
      setPending(null);
      setBusy(false);
    }
  };

  const toggleEnabled = (c) => requestSave(c.id, toPayload({ ...c, enabled: !c.enabled }), false);

  const move = async (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= campaigns.length) return;
    const before = campaigns;
    const next = [...campaigns];
    [next[index], next[target]] = [next[target], next[index]];
    const renumbered = next.map((c, i) => ({ ...c, priority: i + 1 }));
    setCampaigns(renumbered);
    setStatus('');
    try {
      await reorderPlaybook(renumbered.map((c) => c.id));
    } catch (e) {
      setCampaigns(before);
      setStatus(e.message);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete the campaign "${c.name}"? Its unsent steps stop on every event.`)) return;
    setBusy(true);
    try {
      await deletePlaybookCampaign(c.id);
      setCampaigns((list) => list.filter((x) => x.id !== c.id));
      stopEdit();
    } catch (e) {
      setError(c.id, e.message);
    } finally {
      setBusy(false);
    }
  };

  // A new card opens at the top of the list and scrolls into view, so a click
  // on "New campaign" is never answered by an editor opening off-screen.
  const newCardRef = useRef(null);
  useEffect(() => {
    if (editingId === NEW_ID) newCardRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [editingId]);

  const newCampaign = () => {
    const blank = blankCampaign();
    setEditingId(NEW_ID);
    setDraft(blank);
    setError(NEW_ID, '');
  };

  const renderCard = (c, index) => {
    const editing = editingId === c.id;
    const isNew = c.id === NEW_ID;
    const shown = editing ? draft : c;
    return (
      <section key={c.id} className={styles.card} data-testid="campaign-card">
        <header className={styles.cardHeader}>
          <span className={styles.badge}>{isNew ? '+' : index + 1}</span>
          <h3 className={styles.cardTitle}>{shown.name || 'New campaign'}</h3>
          {!isNew && (
            <span className={styles.moveBtns}>
              <button
                type="button"
                className={styles.iconBtn}
                aria-label="Move up"
                title="Move up"
                disabled={index === 0}
                onClick={() => move(index, -1)}
              >
                ↑
              </button>
              <button
                type="button"
                className={styles.iconBtn}
                aria-label="Move down"
                title="Move down"
                disabled={index === campaigns.length - 1}
                onClick={() => move(index, 1)}
              >
                ↓
              </button>
            </span>
          )}
          {!isNew && (
            <button
              type="button"
              role="switch"
              aria-checked={!!c.enabled}
              aria-label={`${c.name} enabled`}
              className={`${styles.switch} ${c.enabled ? styles.switchOn : ''}`}
              onClick={() => toggleEnabled(c)}
              disabled={busy}
            >
              {c.enabled ? 'on' : 'off'}
            </button>
          )}
        </header>
        {editing ? (
          <CampaignEditor
            draft={draft}
            setDraft={setDraft}
            error={errors[c.id]}
            busy={busy}
            invalid={editorInvalid}
            onValidityChange={setEditorInvalid}
            onSave={() => requestSave(c.id, toPayload(draft), isNew)}
            onCancel={stopEdit}
            onDelete={isNew ? null : () => remove(c)}
          />
        ) : (
          <CampaignView
            campaign={c}
            error={errors[c.id]}
            busy={busy || editingId !== null}
            onEdit={() => startEdit(c)}
          />
        )}
      </section>
    );
  };

  return (
    <Page>
      <PageHeader
        title="Campaigns"
        subtitle="Automatic reminders for events with Automatic campaigns on. The higher card wins when two steps collide. Edits apply to active events' unsent steps, except steps an event has overridden."
        actions={
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={newCampaign}
            disabled={editingId !== null}
            title={editingId !== null ? 'Finish editing the open card first' : undefined}
          >
            New campaign
          </button>
        }
      />
      {status && <div className={styles.error}>{status}</div>}
      {loading ? (
        <Loader />
      ) : (
        <div className={styles.cards}>
          {editingId === NEW_ID && draft && <div ref={newCardRef}>{renderCard(draft, campaigns.length)}</div>}
          {campaigns.map(renderCard)}
        </div>
      )}
      {pending && (
        <ImpactModal
          impact={pending.impact}
          busy={busy}
          invalid={pending.id === editingId && editorInvalid}
          onConfirm={confirmSave}
          onCancel={() => setPending(null)}
        />
      )}
    </Page>
  );
}
